---
title: Calls I wish had well-defined parts
subtitle: another square dancing post
date: 2025-07-12 17:22
---

import { Footnote, Footnotes } from "#components/Footnote";
import { Formation, FormationTable } from "#components/Formation";

I attended IAGSDC 2025 a week ago, making it my second square dance convention, after [IAGSDC 2024.](/post/iagsdc-2024/) After my post [Redefining Recycle,](/post/redefining-recycle/) I've been thinking about more redefinitions. I mostly write [challenge square dance material,](/post/square-dance-material/) so I enjoy thinking about calls with parts, and how I can apply concepts to some of these parts, to highlight the [non-compositionality of square dancing](/post/non-compositional-squares/) in <Footnote content={`This paragraph links to every directly-related-to-square-dancing blog post I've made so far. It's good SEO, I'm told.`}>an interesting way.</Footnote>

The thing is, there's lots of calls that *feel* like they have parts, but don't have parts that <Footnote content={`For my purposes, I consider everything in [Vic Ceder's list](https://www.ceder.net/def/callparts.php) to have uncontroversial parts. If Ceder's definition says "*call* is an n-part call", then I also consider it to have uncontroversial parts. Finally, if [SD](https://challengedance.org/sd/) splits a call into parts (i.e., it has the \`visible_fractions\` tag), then I almost always consider it uncontroversial.`}>everyone agrees on.</Footnote> So here's some calls whose parts that I wish everyone agreed to.

Thanks to some great discussion in the Tech Squares Discord for inspiring some of this, and thanks to Andy Tockman for reviewing.

## What makes a call's parts good?

Given any call, there's multiple ways to divide it into parts. Which of these do we choose, if any? This is feelings-based for sure, but here's some of the criteria I've used for judgment.

**Neat, well-defined parts.** As a baseline, I want parts to be well-defined. That is, each of these parts can stand alone, without referring to the previous parts. Consider this proposed parts division of [Turn and Deal:](https://www.ceder.net/def/turnanddeal.php?language=usa&level=A1)

1. 1/2 Tag.
2. Turn 1/4 toward your original turning direction.

<FormationTable
  content={[
    ["1^ 2v 3^ 4v", "before"],
    ["1> 2> / 3< 4<", "1/2 Tag"],
    ["1v 2^ / 3v 4^", "1/4 to Handhold"],
  ]}
/>

The second part here isn't well-defined---it relies on the "original turning direction", which doesn't <Footnote content={`This is fuzzy, though. [Hubs Trade Back](https://www.ceder.net/def/hubsrims.php?language=usa&level=C2) refers to the "original centers" in its definition, but many C2 dancers will say that it has well-defined parts. Andy gives more examples: [Patch [des],](https://www.ceder.net/def/patch.php?language=usa&level=master) [Bounce [des],](https://www.ceder.net/def/bounce.php?language=usa&level=master) [[des] Tie,](https://www.shabsin.com/~dana/squares/c4/book/calls/anyone_Tie.html) and [Reverse the Pass.](https://www.ceder.net/def/reversethepass.php?language=usa&level=master)`}>exist in general.</Footnote> So while I'm fine with this as a *definition* of Turn and Deal, I don't like it as its *parts*.

The other part of the criterion is that the parts should be *neat*. Parts should have succinct definitions that describe reasonable dance motions. Another thing that makes a part neat is if it can be called from many different formations, or with many different concepts. I also only consider divisions that have an equal number of parts for everyone.

See also the discussion in the chapter Fractions and Parts of [Beyond the Basics of C4.](https://challengedance.org/sd/book3.pdf)

**Consistency with existing usage.** If callers use [Finish](https://www.ceder.net/def/finish.php?language=usa&level=master) [call], then the parts of [call] should be defined so that Finish [call] has the same result. The same thing holds for [Like a](https://www.shabsin.com/~dana/squares/c4/book/calls/Like_a.html) [call], or fractions like 1/2 [call].

**Reasonable intermediate formations.** Each part of the call should end in a "reasonable" formation. This has several perks: it makes the call have good fractions; it makes the call play well with parts-based concepts; and coupled with the parts having equal dancer timing, it gives dancers checkpoints within the call, which could make it easier to dance.

**Consistency with other calls.** Ideally, variations of a call should be the same as the original, but with a modification of a single part. Another goal is to systematize similar calls by breaking them into similar parts.

**Equal dancer timing.** Each part should take roughly the same amount of time for all of the active dancers. If the call were to be danced part-by-part, then active dancers shouldn't have to wait too long for other dancers.

This is sometimes referred to as *timing out well*, mostly in reference to calls that don't time out well. [Peel Chain Thru](https://www.ceder.net/def/peelchainthru.php?language=usa&level=C3A) is notorious for bad timing, as its ends' part is slower than its centers' part. This discrepancy is noticeable enough that the callback I often hear is "this call... times out... so well!"

## Plus calls

<a id="follow-your-neighbor" />

**Follow Your Neighbor.** I'm torn about this one, but I'll argue that it should be a two-part call.

1. 1/2 Box Circulate.
2. Centers Cast Off 3/4, ends U-Turn Back.

<FormationTable
  content={[
    ["1^ 2v / 3^ 4v", "before"],
    ["1> / 3^ 2v / 4<", "1/2 Box Circulate"],
    ["1< / 2> / 3< / 4>", "Centers Cast, ends U-Turn"],
  ]}
/>

Why use 1/2 Box Circulate, rather than the Extend in the typical definition? If the first part was an Extend, the second part would need to have the ends <Footnote content={`See [C4 Calls,](https://challengedance.org/sd/book1.pdf) under Terminology → Other Terminology → Quarter to Handhold.`}>1/4 to Handhold,</Footnote> which makes it ill-defined.

This definition doesn't work in the case where it starts from a 1/4 tag. I consider this to be an acceptable sacrifice. The choice of ends U-Turn Back over ends Roll twice is kinda arbitrary, but it does add more applications. The reason I'm torn is that Follow Your Neighbor <Footnote content={`Andy agrees: "Follow Your Neighbor is Scoot Back but more, and so my brain doesn't like 1/2 Follow Your Neighbor being 1/2 Scoot Back but less."`}>feels like a primitive,</Footnote> indivisible unit---and yet, Slide Thru has two parts, so why not Follow Your Neighbor?

Note that [Cross Your Neighbor](https://www.ceder.net/def/crossyourneighbor.php?language=usa&level=master) is [Initially](https://www.ceder.net/def/initially.php?language=usa&level=master) <Footnote content={`Through this post, I'll be using the concise modifiers Add, Omit, and Use, as described in [C4 Calls,](https://challengedance.org/sd/book1.pdf) under Concise Modifiers → Meta-Concept Method.`}>Add</Footnote> centers Spread, Follow Your Neighbor. [Grand Follow Your Neighbor](https://www.ceder.net/def/grandfyn.php?language=usa&level=master) is Initially Use 1/2 Circulate, Follow Your Neighbor.

[Swap the Top](https://www.ceder.net/def/swapthetop.php?language=usa&level=master) is Initially Use 1/2 [Swap Around,](https://www.ceder.net/def/swaparound.php?language=usa&level=master) Follow Your Neighbor. Compare this to the Extend version of the call---you would have to say belles Extend, which obscures the relationship to Swap Around.

<FormationTable
  content={[
    ["1v 2v / 3^ 4^", "before"],
    ["2< / 1v 4^ / 3>", "1/2 Swap Around"],
    ["2> / 1< / 4> / 3<", "Centers Cast, ends U-Turn"],
  ]}
/>

Finish Follow Your Neighbor can be called from a lot of places. From columns, Initially [Tandem](https://www.ceder.net/def/tandem.php?language=usa&level=master) Follow Your Neighbor ends in a bone. From two-faced lines, Initially [As Couples](https://www.ceder.net/def/ascouples.php?language=usa&level=master) Follow Your Neighbor breaks the <Footnote content={`The gestalt of a call is its essence; some calls have a stronger gestalt than others. See [Musings on Definitions,](https://fortytwo.ws/~cbaker/musings-on-definitions.html) starting with "I call the essence of a call its gestalt…"`}>gestalt of the call</Footnote> in an interesting way.

<FormationTable
  content={[
    ["1^ 2^ 3v 4v / 5^ 6^ 7v 8v", "before"],
    ["1> / 2> / 5^ 6^ 3v 4v / 7< / 8<", "As Couples 1/2 Box Circulate"],
    ["1< / 2< / 5> 3< / 6> 4< / 7> / 8>", "Centers Cast, ends U-Turn"],
  ]}
/>

**Relay the Deucey.** Ceder gives a six-part definition of [Relay the Deucey,](https://www.ceder.net/def/relaythedeucey.php?language=usa&level=PLUS) but does not call it a six-part call. Here's a <Footnote content={`I first heard this from Andy, who maintains several of these [alternate definitions.](https://tck.mn/squares/definitions/)`}>succinct restatement</Footnote> of that definition:

1. Arm Turn 1/2.
2. Centers Cast Off 3/4, ends Phantom Hourglass Circulate.
3. Wave of six Trade.
4. [Acey Deucey.](https://www.ceder.net/def/aceydeucey.php?language=usa&level=master)
5. Wave of six Trade.
6. Centers Cast Off 3/4, ends Phantom Hourglass Circulate.

The fourth part here has the ends do a Diamond Circulate on the outside; an unusual use, but I've heard it before. The second and sixth parts are what's known, among Tech Squares circles at least, as *Finish It*, and it appears in calls like [Relay the Top](https://www.ceder.net/def/relaythetop.php?language=usa&level=master) and [Tally Ho.](https://www.ceder.net/def/tallyho.php?language=usa&level=master) Its definition isn't *quite* <Footnote content={`I'll probably have to write another post about this, but briefly: the two big issues are that the the centers and ends division isn't quite correct, and the Phantom Hourglass Circulate doesn't quite end on the right spots. If you consider all the places Finish Tally Ho gets called from you'll see why. See also Andy's page on [Systemizations.](https://tck.mn/squares/systemizations/)`}>what I wrote here.</Footnote> But Finish It appears enough times in this post that I'll always state it as "Centers Cast Off 3/4, ends Phantom Hourglass Circulate", even if it's inaccurate, and you can mentally correct it in your head.

**Spin Chain the Gears, Spin Chain and Exchange the Gears.** Ceder also gives a <Footnote content={`From Andy: "There's an Osamu [Miyabe] tape I have dance to where the first words out of his mouth are 'bow to your partner, bow to your corner, Spin Chain the Gears has six parts.' Very ominous; it was great."`}>six-part definition</Footnote> of [Spin Chain the Gears,](https://www.ceder.net/def/spinchainthegears.php?language=usa&level=PLUS) and I think it should also be an explicit six-part call. SD agrees in this case. Restating it via diamonds:

1. Arm Turn 1/2.
2. Centers Cast Off 3/4, ends U-Turn Back.
3. Very centers Trade.
4. Diamond Circulate Thrice.
5. Very centers Trade.
6. Centers Cast Off 3/4, ends U-Turn Back.

For the similar call [Spin Chain and Exchange the Gears,](https://www.ceder.net/def/spinchainandexchange.php?language=usa) Ceder gives an alternate definition using the C1 formation [Interlocked Diamonds,](https://www.ceder.net/def/interlockeddiamond.php?language=usa&level=C1) and the C2 call [Exchange the Diamond.](https://www.ceder.net/def/exchdiamond.php?language=usa) I think this works well as parts:

1. Arm Turn 1/2.
2. Centers Cast Off 3/4, ends U-Turn Back.
3. Very centers Trade.
4. Diamond Circulate Thrice.
5. Exchange the Diamond 3/4.
6. Interlocked Flip the Diamond.

As justification, Andy reminds me that, at a Plus dance we went to, Ray Brendzy called "Spin Chain and Exchange the Gears, replace the final Flip the Diamond with Cut the Diamond."

One reason to split this into parts is so you can call it from a tidal wave, via [Finally](https://www.ceder.net/def/finally.php?language=usa&level=master) Use [Reverse Flip the Diamond,](https://www.ceder.net/def/reversecutthediamond.php?language=usa&level=master) Spin Chain and Exchange the Gears. Why would I want this? Well, the similar call Spin Chain the Gears *can* be called from tidal waves, though mostly as a gimmick, and it'd be nice if we can do that for Spin Chain and Exchange the Gears too.

<FormationTable
  content={[
    ["1> / 2< / 3> / 4< / 5> / 6< / 7> / 8<", "before"],
    ["2< / 1> / 4< / 3> / 6< / 5> / 8< / 7>", "Arm Turn 1/2"],
    ["2> / 1^ 4v / 3< / 6> / 5^ 8v / 7<", "Centers Cast, ends U-Turn"],
    ["2> / 1^ 4v / 6< / 3> / 5^ 8v / 7<", "VCers Trade"],
    ["4> / 2^ 6v / 1< / 8> / 3^ 7v / 5<", "Diamond Circ Thrice"],
    ["3< / 8v 5^ / 2< / 7> / 4v 1^ / 6>", "3/4 Exchange"],
    ["8^ 2^ 3v 5v / 4^ 6^ 7v 1v", "Reverse Flip"],
  ]}
/>

Given this division into parts, you could call, from twin diamonds, skip the first half, Spin Chain and Exchange the Gears. Not only can you call it from normal diamonds, but you can also call it <Footnote content={`This would be more interesting if Two-Faced didn't exist. But Two-Faced Spin Chain and Exchange the Gears already has facing diamonds in it.`}>from facing diamonds.</Footnote>

<FormationTable
  content={[
    ["1< . 5< / 2^ 3v 6^ 7v / 4> . 8>", "before"],
    ["3> . 7> / 4v 1^ 8v 5^ / 2< . 6<", "Diamond Circ Thrice"],
    ["8> . 4> / 6v 2v 7^ 3^ / 5< . 1<", "3/4 Exchange"],
    ["7< 3< / 8> 4> / 5< 1< / 6> 2>", "Interlocked Flip"],
  ]}
/>

{/* TODO: Wheel and Deal (so Stack the Wheel is secondly single Wheel and Deal lmao) */}

## A1 calls

**Chain Reaction.** Ceder gives a four-part definition of [Chain Reaction,](https://www.ceder.net/def/chain_reaction_a1.php?language=usa&level=A1) and I think it's good. SD agrees in this case.

1. Very centers and those facing Pass Thru, other centers [Counter Rotate.](https://www.ceder.net/def/counterrotate.php?language=usa&level=master)
2. [Checkpointers](https://www.ceder.net/def/checkpoint.php?language=usa&level=master) Hinge.
3. Centers Star 1/4, ends Trade.
4. Centers Cast Off 3/4, ends Phantom Hourglass Circulate.

I think people agree that Chain Reaction has a kinda well-defined first part and last part, so the question is how many parts the middle should be. Reasonable divisions of Chain Reaction would give a total of either four or three parts, and I'm arguing for four parts.

My main argument is via [Relay the Top.](https://www.ceder.net/def/relaythetop.php?language=usa&level=master) Consider that Relay the Top has four parts:

1. Arm Turn 1/2.
2. Centers Cast Off 3/4, ends Phantom Hourglass Circulate.
3. Centers Star 1/4, ends Trade.
4. Centers Cast Off 3/4, ends Phantom Hourglass Circulate.

Then, this four-part division of Chain Reaction shares its last half with Relay the Top. Similarly, you could argue via [The Action,](https://www.shabsin.com/~dana/squares/c4/book/calls/The_Action.html) which also shares its last two parts with Chain Reaction.

From a column, you could call 1/2 Circulate; Finish Chain Reaction. From waves, you could call [Half and Half,](https://www.shabsin.com/~dana/squares/c4/book/calls/Half_and_Half.html) Acey Deucey by Chain Reaction.

<FormationTable
  content={[
    ["1> 2> / 3< 4< / 5> 6> / 7< 8<", "before"],
    ["1> / 7^ 3v 5^ 4v 6^ 2v / 8<", "1/2 Acey Deucey"],
    ["5> / 3^ 7v 8^ 1v 2^ 6v / 4<", "Centers Star 1/4, ends Trade"],
    ["3> 5> / 7< 1< / 8> 2> / 4< 6<", "Finish It"],
  ]}
/>

**Grand Follow Your Neighbor.** I have:

1. 1/2 Circulate.
2. Centers Cast Off 3/4, ends U-Turn Back.

See comments under [Follow Your Neighbor.](#follow-your-neighbor)

<a id="square-chain-thru" />

**Square Chain Thru.** Ceder gives a three-part definition of [Square Chain Thru,](https://www.ceder.net/def/squarechainthru.php?language=usa&level=A1) though I don't believe people consistently agree that this call has parts. His definition also has a different part divsion than what I'd like. He gives the parts as:

1. Right Pull By and 1/4 In.
2. Left Swing Thru.
3. Left Turn Thru.

My preferred parts are instead:

1. Square Thru 2 [to a Wave.](https://www.ceder.net/def/toawave.php?language=usa&level=master)
2. [Any Hand](https://www.ceder.net/def/anyhand.php?language=usa&level=master) Swing Thru.
3. Any Hand Turn Thru.

<FormationTable
  content={[
    ["1v 2v / 3^ 4^", "before"],
    ["4< / 3> / 2< / 1>", "Square Thru 2 to a Wave"],
    ["3< / 1> / 4< / 2>", "Any Hand Swing Thru"],
    ["1< 3> / 2< 4>", "Any Hand Turn Thru"],
  ]}
/>

I first heard an argument for these parts from V. They gave a four-part definition, with what I have as the second part split in half, arguing against the *Left*s in Ceder's definition. I agree; I think the common definition is unnecessarily directional. But we can be more consistent with [Catch [n]](https://www.ceder.net/def/catch.php?language=usa&level=master) by having three parts.

In analogy to Catch [n], which has an extension [Catch [any] [n],](https://www.ceder.net/def/catchanything.php?language=usa&level=master) we can define Square Chain [any] as:

1. Square Thru 2 to a Wave.
2. [any].
3. Any Hand Turn Thru.

Then Square Chain Thru is Square Chain [Any Hand Swing Thru], and [Square Chain the Top](https://www.ceder.net/def/squarechainthetop.php?language=usa&level=master) is Square Chain [Spin the Top.] Square Chain [any] would add some nice variety to openers, complementing <Footnote content={`From waves or inverted lines, you can call either Step and Fold or Any Hand Turn Thru. Step and Fold can be called from normal diamonds, and Any Hand Turn Thru can be called from facing lines or an eight chain.`}>Catch [any] [n].</Footnote> For example, you can call Heads Split Square Chain Relay the Top:

<FormationTable
  content={[
    ["1> 2v 3v 4< / 5> 6^ 7^ 8<", "before"],
    ["6< 4< / 1> 7> / 2< 8< / 5> 3>", "Split Square Thru 2 to a Wave"],
    ["4< 3< / 7> 8> / 1< 2< / 6> 5>", "Relay the Top"],
    ["7< 4> 8< 3> / 6< 1> 5< 2>", "Any Hand Turn Thru"],
  ]}
/>

<a id="transfer-the-column" />

**Transfer the Column.** My proposal for [Transfer the Column](https://www.ceder.net/def/transferthecolumn.php?language=usa&level=master) is:

1. Tandem 1/2 Box Circulate.
2. Centers Cast Off 3/4, ends Counter Rotate.
3. [Drop In.](https://www.ceder.net/def/drop.php?language=usa&level=master)

<FormationTable
  content={[
    ["1> 2> 3> 4> / 5< 6< 7< 8<", "before"],
    ["5^ 1> 2> 3v / 6^ 7< 8< 4v", "Tandem 1/2 Box Circulate"],
    ["6> . 5> / 1^ 7v 2^ 8v / 4< . 3<", "Centers Cast, ends CRot"],
    ["1^ 6v 2^ 5v / 4^ 7v 3^ 8v", "Drop In"],
  ]}
/>

There's a beautiful symmetry between this definition of Transfer the Column, and a definition of its [Rewind,](https://www.shabsin.com/~dana/squares/c4/book/calls/Rewind.html) [Follow Your Leader:](https://www.ceder.net/def/followyourleader.php?language=usa&level=master)

1. 1/2 Box Circulate.
2. Centers Cast Off 3/4, ends Counter Rotate.
3. Tandem Drop In.

<FormationTable
  content={[
    ["1v 6^ 2v 5^ / 4v 7^ 3v 8^", "before"],
    ["6< . 5< / 1v 7^ 2v 8^ / 4> . 3>", "1/2 Box Circulate"],
    ["5v 1< 2< 3^ / 6v 7> 8> 4^", "Centers Cast, ends CRot"],
    ["1< 2< 3< 4< / 5> 6> 7> 8>", "Tandem Drop In"],
  ]}
/>

This symmetry is why I describe the first part of Transfer the Column as Tandem 1/2 Box Circulate, as opposed to \#1 and \#2 [Split Counter Rotate,](https://www.ceder.net/def/splitcounterrotate.php?language=usa&level=master) \#3 and \#4 Circulate, which appears in the definition of [Quick Wrap.](https://www.shabsin.com/~dana/squares/c4/book/calls/Quick_Wrap.html)

Not only can you Rewind each part of the definition, but these two definitions differ only in the placement of the word *Tandem*: whether it's applied Initially or Finally. This also makes the definition of [Split Transfer](https://www.ceder.net/def/boxtransfer.php?language=usa&level=master) evident, and the fact that it is its own Rewind:

1. 1/2 Box Circulate.
2. Centers Cast Off 3/4, ends Split Counter Rotate.
3. Drop In.

<FormationTable
  content={[
    ["1v 2^ / 3v 4^", "before"],
    ["2< / 1v 4^ / 3>", "1/2 Box Circulate"],
    [
      [
        { x: -1, y: 0, direction: "south", label: "2" },
        { x: 0, y: -0.5, direction: "west", label: "1" },
        { x: 0, y: 0.5, direction: "east", label: "4" },
        { x: 1, y: 0, direction: "north", label: "3" }
      ],
      "Centers Cast, ends SpRot"
    ],
    ["1< 3< / 2> 4>", "Drop In"],
  ]}
/>

There's a sacrifice made here, however. Contrast this alternate division of Transfer the Column into parts:

1. Tandem 1/2 Box Circulate.
2. Centers Cast Off 3/4, ends Counter Rotate *and Roll*.
3. *Extend.*

<FormationTable
  content={[
    ["1> 2> 3> 4> / 5< 6< 7< 8<", "before"],
    ["5^ 1> 2> 3v / 6^ 7< 8< 4v", "Tandem 1/2 Box Circulate"],
    ["6v 5v / 1^ 7v 2^ 8v / 4^ 3^", "Centers Cast, ends CRot and Roll"],
    ["1^ 6v 2^ 5v / 4^ 7v 3^ 8v", "Extend"],
  ]}
/>

The advantage of this is that [Transfer and [any]](https://www.ceder.net/def/transferandanything.php?language=usa&level=master) would then be 2/3 Transfer the Column <Footnote content={`This is the C1 modifier But; see [C4 Calls,](https://challengedance.org/sd/book1.pdf) section Concise Modifiers → "But" Modifier for more info.`}>But</Footnote> [any]. With our division into parts, we don't have a succinct way of describing Transfer and [any]. Another sacrifice is that [Open Up the Column](https://www.ceder.net/def/openupthecolumn.php?language=usa&level=master) can no longer be described <Footnote content={`A reasonable attempt is to do Tandem 1/2 Box Circulate, and then the original \#2 U-Turn Back, but this swaps the positions of the ends.`}>analogously to Transfer the Column.</Footnote>

## A2 calls

**Checkmate.** My preferred division for [Checkmate](https://www.ceder.net/def/checkmate.php?language=usa&level=master) is:

1. Circulate twice.
2. \#1 and \#2 1/4 In and Circulate, while the others Circulate twice and 1/4 In.

<FormationTable
  content={[
    ["1> 2> 3> 4> / 5< 6< 7< 8<", "before"],
    ["5> 6> 1> 2> / 7< 8< 3< 4<", "Circulate twice"],
    ["7^ 8^ 5v 6v / 3^ 4^ 1v 2v", "after"],
  ]}
/>

The main reason I want this is to call Finish Checkmate, which is a neat call. You could instead say <Footnote content={`Which makes the definition of [Single Checkmate](https://www.ceder.net/def/singlecheckmate.php) evident as well: Circulate and Split Counter Rotate. Except the new leaders can't roll.`}>Tandem [Twosome](https://www.ceder.net/def/twosome.php?language=usa&level=master) Split Counter Rotate,</Footnote> but that gives the new leaders a Roll direction, when Checkmate doesn't. And I wouldn't want to give up Checkmate and Roll.

<a id="spin-the-windmill" />

**Spin the Windmill [dir].** People agree that, in [Spin the Windmill,](https://www.ceder.net/def/spinthewindmill.php?language=usa&level=master) the centers have three parts. We can also give the ends three parts, if we sacrifice equal dancer timing:

1. Ends turn 1/4 [dir], centers Arm Turn 1/2.
2. Ends Circulate, centers [Slip.](https://www.ceder.net/def/slipslideswingslither.php?language=usa&level=master)
3. Ends Circulate, centers Cast Off 3/4.

<FormationTable
  content={[
    ["1v 2v / 3^ 4v 5^ 6v / 7^ 8^", "before"],
    ["1< . 2< / 4^ 3v 6^ 5v / 7> . 8>", "Ends 1/4 Right, centers Swing"],
    ["2< . 8< / 4^ 6v 3^ 5v / 1> . 7>", "Ends Circulate, centers Slip"],
    ["8< 7< / 6> 5> / 4< 3< / 2> 1>", "Ends Circulate, centers Cast"],
  ]}
/>

For example, from a quarter tag, you could call Spin the Windmill Right, interrupt between each part with Diamond Circulate, which I think is cute. From a quarter tag, you can also kinda call 1/2 Spin the Windmill, which ends in concentric diamonds.

The timing of Spin the Windmill made me realize why several calls have the ends Circulate twice. From a 2×4, if the ends Circulate once, then they travel different distances, but if they Circulate twice, they all walk the same distance. I'm fine sacrificing equal dancer timing, though; it's the last in my criteria because it doesn't make a difference in the unmodified case.

**Split Transfer.** See comments under [Transfer the Column.](#transfer-the-column)

## C1 calls

**Cross Your Neighbor.** I have:

1. 1/2 Box Circulate and Centers Spread.
2. Centers Cast Off 3/4, ends U-Turn Back.

See comments under [Follow Your Neighbor.](#follow-your-neighbor)

**Linear Action, Tally Ho.** Both of these calls have a well-defined first part and a well-defined last part, so we might as well make the middle thing a single part. Thus, for [Linear Action:](https://www.ceder.net/def/linearaction.php?language=usa&level=master)

1. Hinge.
2. Ends Trade, centers Box Circulate 1 and 1/2.
3. Centers Cast Off 3/4, ends Phantom Hourglass Circulate.

And <Footnote content={`SD agrees with me in this part division, which surprises me because I think it's controversial. It's one case where SD is more permissive than most dancers.`}>for [Tally Ho:](https://www.ceder.net/def/tallyho.php?language=usa&level=master)</Footnote>

1. 1/2 Circulate.
2. Ends Trade, centers Hinge and 1/2 Box Circulate.
3. Centers Cast Off 3/4, ends Phantom Hourglass Circulate.

As to why there's only one middle part, it's mostly because there aren't any reasonable subdivisions. From columns, you can call Last 2/3 [Reverse Order](https://www.ceder.net/def/reverseorder.php?language=usa&level=master) Tally Ho to <Footnote content={`Warning: Awkward for the ends.`}>end in an hourglass.</Footnote>

<FormationTable
  content={[
    ["1^ 2v / 3^ 4v / 5^ 6v / 7^ 8v", "before"],
    ["1> / 3^ 2v / 5^ 4v / 7^ 6v / 8<", "1/2 Circulate"],
    [
      [
        { x: -1, y: -1, direction: "north", label: "5" },
        { x: -1, y: 1, direction: "north", label: "8" },
        { x: 1, y: -1, direction: "south", label: "1" },
        { x: 1, y: 1, direction: "south", label: "4" },
        { x: 0, y: -1.5, direction: "east", label: "2" },
        { x: 0, y: -0.5, direction: "west", label: "3" },
        { x: 0, y: 0.5, direction: "east", label: "6" },
        { x: 0, y: 1.5, direction: "west", label: "7" },
      ],
      "Finish It",
    ],
    [
      [
        { x: -1.5, y: -1, direction: "north", label: "1" },
        { x: -1.5, y: 1, direction: "north", label: "4" },
        { x: 1.5, y: -1, direction: "south", label: "5" },
        { x: 1.5, y: 1, direction: "south", label: "8" },
        { x: 0, y: -1.5, direction: "east", label: "3" },
        { x: -0.5, y: 0, direction: "north", label: "7" },
        { x: 0.5, y: 0, direction: "south", label: "2" },
        { x: 0, y: 1.5, direction: "west", label: "6" },
      ],
      "Ends Trade, centers Hinge and 1/2 Box Circ",
    ],
  ]}
/>

From a quarter tag, you can call Finally [Stable](https://www.ceder.net/def/stable.php?language=usa&level=master) 2/3 Linear Action, to end in triple single quarter tags.

**Regroup.** I want [Regroup](https://www.ceder.net/def/regroup.php?language=usa&level=master) to have two parts. This disagrees with SD, and maybe other dancers, who want the ends to have three parts.

1. Ends 1/4 Out, centers Trade.
2. Ends Trade and [Press Out,](https://www.ceder.net/def/press.php?language=usa&level=master) centers Roll.

<FormationTable
  content={[
    ["1^ 2^ 3^ 4^ / 5v 6v 7v 8v", "before"],
    ["1< 3v 2v 4> / 5< 7^ 6^ 8>", "Ends 1/4 Out, centers Trade"],
    ["5> 8< / 3> 2< / 7> 6< / 1> 4<", "Ends Trade and Press Out, centers Roll"],
  ]}
/>

My main reasoning is its *kinda* Rewind, [Trip the Set,](https://www.ceder.net/def/triptheset.php?language=usa&level=master) also has two parts:

1. Ends Cross Fold, centers 1/4 Out.
2. Ends Roll, centers Trade.

<FormationTable
  content={[
    ["5< 8> / 3< 2> / 7< 6> / 1< 4>", "before"],
    ["1> 3^ 2^ 4< / 5> 7v 6v 8<", "Ends Cross Fold, centers 1/4 Out"],
    ["1v 2v 3v 4v / 5^ 6^ 7^ 8^", "Ends Roll, centers Trade"],
  ]}
/>

The Rewinds are technically ambiguous; you can't rewind Regroup because you don't know whether the centers were facing in or out. But from their most common starting position, lines facing out, they satisfy the Rewind property of being related by U-Turn Back.

Andy tells me that the ends' second part of Regroup can also be described as [O Circulate](https://www.ceder.net/def/o.php?language=usa&level=master) Twice. It's a neat observation! I think that the Trade and Press Out better captures the Rewind, but O Circulate Twice is shorter.

The motivation here is consistency, rather than choreography. I've never heard a caller <Footnote content={`And if you're a C3A caller reading this, well, I don't want to give anyone any ideas.`}>modify the parts of Trip the Set,</Footnote> so I'm doubtful anyone would modify these parts of Regroup. Then again, I haven't danced much C3A or C3B, so maybe there *is* choreographic potential.

**Relay the Shadow.** For [Relay the Shadow,](https://www.ceder.net/def/relaytheshadow.php?language=usa&level=master) I'm proposing:

1. Arm Turn 1/2.
2. Center six Cast Off 3/4, very ends Counter Rotate.
3. Centers Hinge and Spread, others do the centers' part of [Cast a Shadow.](https://www.ceder.net/def/castashadow.php?language=usa&level=master)

This is a case where the first part is well-defined, and people kinda agree the next thing is a part. This second part appears in other calls, like [Along.](https://www.ceder.net/def/swingalong.php?language=usa&level=master) But there's no well-defined last part. I don't think there's reasonable subdivisions of the last part here, so I've made it a three-part call.

My main reason for wanting this division is so I can call Like a Relay the Shadow, which is a fun and quick way to turn generalized quarter tags to lines. You can also call it <Footnote content={`There's more or less tasteful ways to call it from an I. Ends facing out want to Cloverleaf into the center, so they can bump into the centers if they're not careful. In the below diagram, dancer 2 and dancer 3 are turning toward the same direction, so they don't bump into each other. But if dancer 1 was facing out, they'd bump into dancer 4 doing the Spread.`}>from an I.</Footnote>

<FormationTable
  content={[
    ["1v 2^ / 3> / 4< / 5> / 6< / 7v 8^", "before"],
    ["4^ 8< 2< 3v / 6^ 7> 1> 5v", "Like a Relay the Shadow"],
  ]}
/>

I've been staring at this trying to figure out how I can call Reverse Order Relay the Shadow from an I, but I can't come up with anything. Exercise for the reader.

**Shakedown.** Clark Baker has [written about defining Shakedown,](https://fortytwo.ws/~cbaker/musings-on-definitions.html) and I will flaunt every single point made in that document to give this definition:

1. As one movement: [Chase the 1/2 Tag.](https://www.ceder.net/def/chasethetag.php?language=usa&level=master)
2. Roll.

<FormationTable
  content={[
    ["1^ 2^ / 3v 4v", "before"],
    ["3^ 1v / 4^ 2v", "Chase the 1/2 Tag"],
    ["3> 1< / 4> 2<", "Roll (as if you could)"],
  ]}
/>

The "as one movement" in the first part is to give dancers Roll directions. They otherwise technically don't have a Roll direction, because the last part of any tagging call has people walk forward. This sidesteps the "turn 1/4 toward your original turning direction" that appears in the definition of, say, [Turn and Deal.](https://www.ceder.net/def/turnanddeal.php?language=usa&level=master)

I think most dancers, if asked to split Shakedown into parts, would pick 1/4 Right, Box Counter Rotate, and Roll. But this is so similar to [Split Sidetrack,](https://www.ceder.net/def/sidetrack.php?language=usa&level=master) and rather than consistency, I want choreography. In fact, I'm proposing that Shakedown be an instance of a general tagging call, say, <Footnote content={`Compare the tagging suffix discussed in [Redefining Recycle,](https://blog.cjquines.com/post/redefining-recycle/#as-a-tagging-call) which is Reflected 1/2 [tag] and Roll, equivalent to a Reflected [tag]down. That gives everyone a roll direction, but [tag]down doesn't always do so.`}>[tag]down.</Footnote>

1. As one movement: [tag] 1/2.
2. Roll.

Shakedown would be Chasedown. Also, note that Recycle is equivalent to <Footnote content={`See Ceder's [Tagging Calls](https://www.ceder.net/def/taggingcalls.php) page to reference which tagging calls these are.`}>Flipdown,</Footnote> and Turn and Deal would be Tagdown. You could also call Trackdown:

<FormationTable
  content={[
    ["1^ 2^ / 3^ 4^ / 5v 6v / 7v 8v", "before"],
    ["8^ 4v 7^ 3v / 6^ 2v 5^ 1v", "Track 2"],
    ["8> 4> 7< 3< / 6> 2> 5< 1<", "Roll (as if you could)"],
  ]}
/>

and Invertdown:

<FormationTable
  content={[
    ["1^ 2v / 3^ 4v / 5^ 6v / 7^ 8v", "before"],
    ["3v 5^ 2v 8^ / 1v 7^ 4v 6^", "Invert 1/2"],
    ["3> 5^ 2v 8< / 1> 7^ 4v 6<", "Roll (as if you could)"],
  ]}
/>

and Loopdown:

<FormationTable
  content={[
    ["1^ 2^ / 3^ 4^", "before"],
    ["3> 1> / 2< 4<", "Loop and Tag 1/2"],
    ["3^ 1^ / 2^ 4^", "Roll (as if you could)"],
  ]}
/>

It's kinda exciting how different these all are. Also, [tag]down is a cool suffix---I might write about it independently.

**Square Chain the Top.** I want:

1. Square Thru 2 to a Wave.
2. Spin the Top.
3. Any Hand Turn Thru.

See comments under [Square Chain Thru.](#square-chain-thru)

## C2 calls

**Along.** [Along](https://www.ceder.net/def/swingalong.php?language=usa&level=master) can be a two-part call:

1. Center six Hinge and Circulate, very ends Counter Rotate.
2. Center six [Grand Peel and Trail,](https://www.ceder.net/def/peelandtrail.php?language=usa&level=master) others Extend.

Similar to Like a Relay the Shadow, this lets us call Like an Along, which is a neat way to turn certain quarter tags to certain lines. Unlike Like a Relay the Shadow, Like an Along can <Footnote content={`Well, a normal Along can end in parallelograms already, but it's still cool that it can do that.`}>end in parallelograms.</Footnote>

<FormationTable
  content={[
    ["1^ 2v / 3^ 4^ 5v 6v / 7^ 8v", "before"],
    ["3^ 1v 2^ 4v . . / . . 5^ 7v 8^ 6v", "Like an Along"],
  ]}
/>

**Grand Chain Eight.** Here's a folklore definition for [Grand Chain Eight:](https://www.ceder.net/def/grandchain8.php?language=usa&level=master)

1. Funny Right Pull By.
2. [Mirror](https://www.ceder.net/def/mirror.php?language=usa&level=master) 1/2 Circulate.
3. Hinge. [Like a Couple Up.](https://www.ceder.net/def/coupleup.php?language=usa&level=master)

I would support dividing it into parts this way too. The third part has two nested parts, giving it a similar structure to <Footnote content={`As a non-C4 dancer, this is the only reason I know Hot Foot Spin. It has nested parts! Oh wow! I think C3A and C3B dancers should get to have fun with nested parts too.`}>the C4 call [Hot Foot Spin.](https://www.shabsin.com/~dana/squares/c4/book/calls/Hot_Foot_Spin.html)</Footnote> From a starting double pass thru, you can call Finally Initially Tandem, Grand Chain Eight.

<FormationTable
  content={[
    ["1v 2v / 3v 4v / 5^ 6^ / 7^ 8^", "before"],
    ["1v 2v / 5^ 6^ / 3v 4v / 7^ 8^", "Funny Right Pull By"],
    ["1v 5^ 2v 6^ / 3v 7^ 4v 8^", "Mirror 1/2 Circulate"],
    ["5> 7< 6> 8< / 1> 3< 2> 4<", "Tandem Hinge, Like a Couple Up"],
  ]}
/>

**Perk Up.** Similar to Relay the Shadow, [Perk Up](https://www.ceder.net/def/perkup.php?language=usa&level=master) a call that has a well-defined first part and a well-defined second part, and the last part doesn't have any natural divisions, so we might as well say:

1. Circulate.
2. 1/2 Split Circulate.
3. Ends Circulate twice, centers Hinge, Box Circulate, Trade.

I mostly want this for the sake of [Central](https://www.ceder.net/def/central.php?language=usa&level=master) Like a Perk Up, which is a nice call, along the lines of Recycle or Linear Cycle. Note that while the third part isn't the same number of parts for everyone, you can halve it, so you can call something like Finally 1/2 Perk Up; from waves, it ends in an hourglass.

<FormationTable
  content={[
    ["1^ 2v 3^ 4v / 5^ 6v 7^ 8v", "before"],
    ["5^ 3v 7^ 1v / 8^ 2v 6^ 4v", "Circulate"],
    ["5> . 7> / 8^ 3v 6^ 1v / 2< . 4<", "1/2 Split Circ"],
    [
      [
        { y: -1.5, x: -1, direction: "east", label: "2" },
        { y: -1.5, x: 1, direction: "east", label: "5" },
        { y: 1.5, x: -1, direction: "west", label: "4" },
        { y: 1.5, x: 1, direction: "west", label: "7" },
        { y: 0, x: -1.5, direction: "north", label: "3" },
        { y: -0.5, x: 0, direction: "east", label: "8" },
        { y: 0.5, x: 0, direction: "west", label: "1" },
        { y: 0, x: 1.5, direction: "south", label: "6" },
      ],
      "Ends Circ, centers Hinge, 1/2 Box Circ"
    ],
  ]}
/>

## C3A calls

This is going to be the longest section---so many calls in this level that I wish had parts but didn't. It's kind of a shame, given that C3A is the level that introduces <Footnote content={`Not that this ever stopped me from saying "do the first part Tandem..." in C1.`}>[Initially](https://www.ceder.net/def/initially.php?language=usa&level=master) and [Finally.](https://www.ceder.net/def/finally.php?language=usa&level=master)</Footnote>

**Breaker.** I'll give what I want the parts of [Breaker](https://www.ceder.net/def/breaker.php?language=usa&level=master) 3 to be, and the other uses of Breaker are analogous:

1. Ends Hinge, centers Box Counter Rotate.
2. Ends Trade, centers 1/2 Box Circulate.
3. Centers Cast Off 3/4, ends Phantom Hourglass Circulate.

This differs from the usual definition of Breaker, in that I'm allowing the ends to start facing the same direction, like they do in [Linear Action.](https://www.ceder.net/def/linearaction.php?language=usa&level=master) That's <Footnote content={`SD allows it with a warning, so someone must've wanted it.`}>not something I've heard before,</Footnote> but because the call's <Footnote content={`In particular, Breaker 3 is Centers Work (Initially Add Box Circulate, Initially Tandem), Linear Action.`}>a lot like Linear Action,</Footnote> I'm fine with this.

From a <Footnote content={`From a column, call ends Hinge; the resulting spots on the floor form a rigger. See [C4 Calls,](https://challengedance.org/sd/book1.pdf) Terminology → Formation Names → Bat or Rigger.`}>rigger</Footnote> with right-hand miniwaves, you can call <Footnote content={`SD gives this a different result, because SD has a different division of parts. It believes that the first part of the ends is Cast Off 3/4, and that the ends do nothing in the second part.`}>Finish Breaker 3.</Footnote>

<FormationTable
  content={[
    [
      [
        { x: -2.5, y: 0, direction: "north", label: "1" },
        { x: -1.5, y: 0, direction: "south", label: "2" },
        { x: -0.5, y: -0.5, direction: "north", label: "3" },
        { x: -0.5, y: 0.5, direction: "north", label: "4" },
        { x: 0.5, y: -0.5, direction: "south", label: "5" },
        { x: 0.5, y: 0.5, direction: "south", label: "6" },
        { x: 1.5, y: 0, direction: "north", label: "7" },
        { x: 2.5, y: 0, direction: "south", label: "8" },
      ],
      "before"
    ],
    ["3> / 2^ 1v 4^ 5v 8^ 7v / 6<", "Ends Trade, centers 1/2 Box Circ"],
    ["2> 3> / 1< 5< / 4> 8> / 6< 7<", "Finish It"],
  ]}
/>

**Delight, Dilemma.** Here's my parts for [Delight;](https://www.ceder.net/def/delight.php?language=usa&level=master) Dilemma is analogous.

1. Ends turn 1/4 Right, centers Arm Turn 1/2.
2. Ends Circulate, centers Slip twice.
3. Ends Circulate, centers Cast Off 3/4.

The parts are chosen for consistency with my parts for [Spin the Windmill,](#spin-the-windmill) so see my comments under that entry.

**Follow Your Leader.** See comments under [Transfer the Column.](#transfer-the-column)

**Peel Chain Thru.** Several trade-offs here, but I'm settling with:

1. Ends [Concentric](https://www.ceder.net/def/concentric.php?language=usa&level=master) Leads U-Turn Back outward and Touch, centers Arm Turn 1/2.
2. Ends Arm Turn 1/2, centers Slip.
3. Fan the Top.

<FormationTable
  content={[
    ["1^ 2^ / 3^ 4v 5^ 6v / 7v 8v", "before"],
    ["7^ 1v 4^ 3v 6^ 5v 8^ 2v", "Ends Cast Back and Touch, centers Swing"],
    ["1^ 7v 4^ 6v 3^ 5v 2^ 8v", "Ends Swing, centers Slip"],
    ["1> 3> / 7< 5< / 4> 2> / 6< 8<", "Fan the Top"],
  ]}
/>

I was torn about how to define the first part for the ends. I considered, as alternatives, [Cast Back](https://www.ceder.net/def/castback.php?language=usa&level=master) and Touch, 1/2 Zoom, and Outward [Roll to a Wave.](https://www.ceder.net/def/rightrolltoawave.php?language=usa&level=master) The first is the CALLERLAB definition, but it felt unnecessarily limited. The second is improper; you can't call 1/2 Zoom <Footnote content={`Kind of. Note that Plenty from an hourglass ends with the centers back-to-back before doing a Right Roll to a Wave. If we believe the CALLERLAB definition, where the last part of the centers is 1/2 Zoom, this should be illegal. But Sandie called it last weekend and people danced it. There's a longer discussion here...`}>from dancers back-to-back.</Footnote> The third has precedent in the description of [Rally,](https://www.ceder.net/def/rally.php?language=usa&level=master) where the final part has an Inward Roll to a Wave, but I've found no examples of Rally being called where those who do the Inward Roll start as back-to-back. I settled with this, even if it's wordy.

Which leads to another question: should the ends Touch at the end of the first part, or the start of the second part? The timing works better in the latter. But I chose the former because it ends in a tidal wave, which is more useful. It makes 1/3 Peel Chain Thru an okay way to go from a quarter tag to a tidal wave.

**Rally.** Everyone agrees the centers have two parts, but what about the ends? I think they also have two parts, which makes these the parts of [Rally:](https://www.ceder.net/def/rally.php?language=usa&level=master)

1. Little.
2. Centers Peel and Trail, ends Inward Roll to a Wave.

The alternative is the ends having three parts: 1/4 Right, Counter Rotate, Inward Roll to a Wave. But I think this obscures the relationship to Little. As a consequence, Rally becomes more versatile; you can call things like Finally Central Rally.

<FormationTable
  content={[
    ["1^ 2^ / 3^ 4v 5^ 6v / 7v 8v", "before"],
    ["7^ 3v 5^ 1v / 8^ 4v 6^ 2v", "Little"],
    ["7v 3^ 8v 4^ 5v 1^ 6v 2^", "Peel and Trail"],
  ]}
/>

**Spin Chain the Line.** [Spin Chain the Line](https://www.ceder.net/def/spinchaintheline.php?language=usa&level=master) is another case where there's a well-defined first part, and people will agree on the last part, so let's take the middle as a second part.

1. Arm Turn 1/2.
2. Checkpointers Cast Off 3/4 and Spread, very centers Trade.
3. Centers Cast Off 3/4.

From lines facing out, have the center beaus U-Turn Back, then all 1/2 Circulate. You can then call <Footnote content={`I wish there was a shorter way to say this. It's "do the middle part" in English. But the language of square dancing isn't English, despite appearances; you can call Square Thru 4, but you can't call Square Thru 5.`}>Like a 2/3 Spin Chain the Line</Footnote> to end in an hourglass.

<FormationTable
  content={[
    [
      [
        { x: -3, y: 0, direction: "north", label: "2" },
        { x: -2, y: 0, direction: "south", label: "3" },
        { x: -1, y: 0, direction: "north", label: "4" },
        { x: 0, y: -0.5, direction: "east", label: "1" },
        { x: 0, y: 0.5, direction: "west", label: "8" },
        { x: 1, y: 0, direction: "south", label: "5" },
        { x: 2, y: 0, direction: "north", label: "6" },
        { x: 3, y: 0, direction: "south", label: "7" },
      ],
      "before"
    ],
    [
      [
        { y: -1.5, x: -1, direction: "west", label: "3" },
        { y: -1.5, x: 1, direction: "west", label: "5" },
        { y: 1.5, x: -1, direction: "east", label: "4" },
        { y: 1.5, x: 1, direction: "east", label: "6" },
        { y: 0, x: -1.5, direction: "north", label: "2" },
        { y: -0.5, x: 0, direction: "east", label: "8" },
        { y: 0.5, x: 0, direction: "west", label: "1" },
        { y: 0, x: 1.5, direction: "south", label: "7" },
      ],
      "Checkpointers Cast and Spread, vcers Trade"
    ],
  ]}
/>

From point-to-point diamonds, [Stingy](https://www.ceder.net/def/generous.php?language=usa&level=master) Like a 2/3 Spin Chain the Line also ends in an hourglass.

**Swap the Top.** I want:

1. 1/2 Swap Around.
2. Centers Cast Off 3/4, ends U-Turn Back.

See comments under [Follow Your Neighbor.](#follow-your-neighbor)

**The Gamut.** Another with ends Circulate twice, which I'll split across a part boundary. It's fine; [The Gamut](https://www.ceder.net/def/thegamut.php?language=usa&level=master) <Footnote content={`I don't know why Peel Chain Thru has such a reputation for bad timing, when The Gamut's timing is worse. I think the Trade the Wave shouldn't exist. Sorry Lee Kopman.`}>has bad timing anyway.</Footnote>

1. Ends Circulate, centers Any Hand 3/4 Thru.
2. Ends Circulate, centers Trade the Wave.
3. Cut the Diamond.

<FormationTable
  content={[
    ["1> 2> / 3< 4< / 5> 6> / 7< 8<", "before"],
    ["7> . 1> / 5v 6^ 3v 4^ / 8< . 2<", "Ends Circ, centers 3/4 Thru"],
    ["8> . 7> / 3^ 4v 5^ 6v / 2< . 1<", "Ends Circ, centers Trade the Wave"],
    ["3> 5> / 2> 1> / 8< 7< / 4< 6<", "Cut the Diamond"],
  ]}
/>

All the choreographic ideas I have are similar to the ones I have for Spin the Windmill. You can interrupt between each part with a Diamond Circulate, I guess?

**Triple Play.** These parts for [Triple Play](https://www.ceder.net/def/tripleplay.php?language=usa&level=master) are gonna look weird until I explain them, but trust me:

1. 1/2 Circulate. Center six Trade, very ends Counter Rotate.
2. Center six 1/2 Circulate. Centers Hinge, ends Phantom Hourglass Circulate.
3. Drop In.

<FormationTable
  content={[
    ["1> 2> 3> 4> / 5< 6< 7< 8<", "before"],
    ["5> / 6> 7> 8> / 1< 2< 3< / 4<", "1/2 Circ, Triple Trade, vends Counter Rotate"],
    ["1> . 5> / 2^ 6v 3^ 7v / 4< . 8<", "Center six 1/2 Circ, centers Hinge, ends Finish It"],
    ["2^ 1v 3^ 5v / 4^ 6v 8^ 7v", "Drop In"],
  ]}
/>

The first part ends in a quarter tag, with a wave between two parallel mini-waves. The second part ends in diamonds, and the third part ends in waves. The main application I'm thinking of is Finish Triple Play, which I can only think of that position to call from, but it's *such* a good dance motion.

Why three parts, rather than subdividing into more? Because it matches the CALLERLAB definition, which is:

1. \#1 Transfer, others 1/2 Circulate and Trade.
2. In the center column: \#1 Transfer, others 1/2 Circulate and Hinge.
3. Extend.

The <Footnote content={`A subtler difference is the position of the original \#1 dancer. If we divided according to the CALLERLAB definition, then 1/3 Triple Play *doesn't* end in quarter tag spots; the very outside dancers would be a half-matrix-spot out. This is silly, and we should end in a nice formation, because we *can*.`}>main difference</Footnote> is that, in this definition, those doing the transfer Roll after their parts, and the call ends with an Extend; I chose to omit the Rolls and end with a Drop In; for reasoning, see my comments under [Transfer the Column.](#transfer-the-column)

**Wind the Bobbin.** I've heard Finish [Wind the Bobbin](https://www.ceder.net/def/windthebobbin.php?language=usa&level=master) called before, and I don't see a reason to divide the rest of the call into parts, so I present:

1. 1/2 Zoom.
2. Ends Circulate twice, centers Cast Off 3/4, Slip, Cast Off 3/4.

From columns, you could call 3/4 Wind the Bobbin, which ends in an hourglass.

<FormationTable
  content={[
    ["1> 2> 3> 4> / 5< 6< 7< 8<", "before"],
    ["2< 4< / 1> 3> / 6< 8< / 5> 7>", "1/2 Zoom"],
    [
      [
        { y: -1.5, x: -1, direction: "west", label: "4" },
        { y: -1.5, x: 1, direction: "west", label: "7" },
        { y: 1.5, x: -1, direction: "east", label: "2" },
        { y: 1.5, x: 1, direction: "east", label: "5" },
        { y: 0, x: -1.5, direction: "north", label: "1" },
        { y: -0.5, x: 0, direction: "west", label: "3" },
        { y: 0.5, x: 0, direction: "east", label: "6" },
        { y: 0, x: 1.5, direction: "south", label: "8" },
      ],
      "Ends Circ, centers Cast and 1/2 Slip",
    ],
  ]}
/>

I would prefer if the parts were [Peel to a Diamond;](https://www.ceder.net/def/peeltoadiamond.php?language=usa&level=master) Spin the Windmill Forward. It's succinct, and gives Wind the Bobbin nested parts via [Spin the Windmill.](#spin-the-windmill) But it goes against the existing use of Finish Wind the Bobbin, so whatever.

## C3B calls

**Divide the Ocean [any], Divide the Sea [any].** I'm surprised [Divide the Ocean](https://www.ceder.net/def/dividetheocean.php?language=usa&level=master) doesn't have parts. To me, it seems clear that the ends have two parts, and the centers have four parts, so it's a two part call with nested parts for the centers. Here's Divide the Ocean Right; variations are split analogously.

1. Ends Cast Off 3/4, centers Hinge and Partner Tag.
2. Ends [Cross Concentric](https://www.ceder.net/def/crossconcentric.php?language=usa&level=master) Vertical 1/2 Tag, centers Press Ahead and 1/4 Right.

Then, 1/2 Divide the Ocean would be a versatile call. You could call it from quarter tags with the ends in mini-waves.

<FormationTable
  content={[
    ["1> / 2> 3< 4> / 5< 6> 7< / 8<", "before"],
    [
      [
        { x: -2.5, y: 0, direction: "north", label: "2" },
        { x: -1.5, y: 0, direction: "south", label: "5" },
        { x: -0.5, y: -0.5, direction: "west", label: "1" },
        { x: -0.5, y: 0.5, direction: "west", label: "6" },
        { x: 0.5, y: -0.5, direction: "east", label: "3" },
        { x: 0.5, y: 0.5, direction: "east", label: "8" },
        { x: 1.5, y: 0, direction: "north", label: "4" },
        { x: 2.5, y: 0, direction: "south", label: "7" },
      ],
      "before"
    ],
  ]}
/>

While thinking of other places you could call 1/2 Divide the Ocean, I thought of concentric diamonds. But then I realized that you can *already* call Divide the Ocean Right <Footnote content={`I'm surprised I haven't seen this before. Not in Ceder's notes, not in Taminations, not in discussion, not from a caller. SD lets you do it though.`}>from concentric diamonds.</Footnote>

**Reactivate.** I'm also surprised [Reactivate](https://www.ceder.net/def/reactivate.php?language=usa&level=master) doesn't have parts. It starts like a Chain Reaction and ends like a Coordinate, so:

1. Very centers and those facing Pass Thru, ends of the center four Counter Rotate.
2. Center six Trade.
3. Very centers and very ends Phantom Hourglass Circulate.

I want this for consistency with the definitions for Chain Reaction and Coordinate. The thing is, I'm not sure what you *can* do with the parts of Reactivate that you can't already do with Chain Reaction or Coordinate. I guess you can call [Secondly](https://www.ceder.net/def/secondly.php?language=usa&level=master) Omit Reactivate pretty much everywhere you can call Reactivate.

**Stimulate.** I'm somewhat surprised [Stimulate](https://www.ceder.net/def/stimulate.php?language=usa&level=master) doesn't have parts, but not too surprised. A lot of people think of the centers as ending with a Grand Hinge the Top. I think of the call as ending with a *Finish It*:

1. Circulate.
2. 1/2 Circulate.
3. Center six Hinge, very ends Counter Rotate.
4. Centers Cast Off 3/4, ends Phantom Hourglass Circulate.

From a column, you can call <Footnote content={`If you disagree with my use of Split as a concept, you can pronounce this as Secondly Use 1/2 Split Circulate, and use three more words to do so.`}>Secondly Split</Footnote> 3/4 Stimulate.

<FormationTable
  content={[
    ["1^ 2v / 3^ 4v / 5^ 6v / 7^ 8v", "before"],
    ["3^ 1v / 5^ 2v / 7^ 4v / 8^ 6v", "Circulate"],
    ["3> / 5^ 1v / 2< / 7> / 8^ 4v / 6<", "1/2 Split Circulate"],
    ["5> / 1< / 6^ 2v 7^ 3v / 8> / 4<", "Center six Hinge, vends CRot"],
  ]}
/>

From a quarter tag, you can call Finish Finish Stimulate. From lines facing out, you can call 3/4 Stimulate.

<Footnotes />
